import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin,
  Truck,
  CheckCircle,
  XCircle,
  Clock,
  Search,
  Eye,
  ArrowRight,
  DollarSign,
  X,
  Loader2,
  AlertCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import DashboardLayout from "../../components/DashboardLayout";
import FleetrifyCard from "../../components/Card";
import { FleetrifyModal } from "../../components/Modal/modal";
import InfoTooltip from "../../components/Tooltip/InfoTooltip";
import { PaymentBadges } from "../../components/PaymentBadges";
import {
  RelocationOrder,
  RelocationOrdersParams,
  useGetRelocationOrdersQuery,
  useGetRelocationOrderByIdQuery,
  useAcceptRelocationOrderMutation,
  useRejectRelocationOrderMutation,
  useCounterRelocationOrderMutation,
} from "../../redux/services/relocationApi";
import { usePermission } from "../../utils/permissions";
import {
  negotiationHint,
  NegotiationRequestBadge,
  NegotiationRequestNotice,
  isNegotiationRequestOpen,
  showNegotiationBadge,
} from "../../components/NegotiationRequest";

// The API's reason for a refusal, e.g. accepting while the customer's
// negotiation request is still open (400).
const apiErrorMessage = (err: any, fallback: string) => {
  const raw = err?.data?.error?.message ?? err?.data?.message;
  if (typeof raw === "string" && raw.trim()) return raw;
  if (raw && typeof raw === "object") {
    const first = Object.values(raw as Record<string, string[] | string>)[0];
    const text = Array.isArray(first) ? first[0] : first;
    if (typeof text === "string" && text.trim()) return text;
  }
  return fallback;
};

// ── Types ─────────────────────────────────────────────────────────────────────

type OrderStatus = RelocationOrder["status"];
type OrderType = RelocationOrder["order_type"];
type ActionMode = "accept" | "reject" | "counter" | null;
type StatusFilter = OrderStatus | "all";
type TypeFilter = OrderType | "all";

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatCurrency = (val: string | number) => {
  const n = typeof val === "string" ? parseFloat(val) : val;
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    maximumFractionDigits: 0,
  }).format(n || 0);
};

const formatDate = (val: string | null) => {
  if (!val) return "—";
  const d = new Date(val);
  return isNaN(d.getTime())
    ? val
    : d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

const formatMoveSize = (val: string | null) => {
  if (!val) return null;
  return val.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const getDisplayId = (order: RelocationOrder) =>
  order.order_number || order.id.slice(0, 8).toUpperCase();

// ── Config ────────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  OrderStatus,
  { label: string; bg: string; text: string }
> = {
  pending: { label: "Pending", bg: "bg-yellow-100", text: "text-yellow-800" },
  accepted: { label: "Accepted", bg: "bg-green-100", text: "text-green-800" },
  rejected: { label: "Rejected", bg: "bg-red-100", text: "text-red-800" },
  counter_sent: {
    label: "Counter Sent",
    bg: "bg-blue-100",
    text: "text-blue-800",
  },
  customer_countered: {
    label: "Customer Countered",
    bg: "bg-orange-100",
    text: "text-orange-800",
  },
  customer_accepted: {
    label: "Customer Accepted",
    bg: "bg-green-100",
    text: "text-green-800",
  },
  delivered: { label: "Delivered", bg: "bg-gray-100", text: "text-gray-700" },
  picked_up: {
    label: "Picked Up",
    bg: "bg-indigo-100",
    text: "text-indigo-800",
  },
};

const STATUS_EXPLANATIONS: Partial<Record<OrderStatus, string>> = {
  counter_sent: "You've sent a new price to the customer. Waiting for them to accept, reject, or counter back.",
  customer_countered: "The customer rejected your counter price and proposed their own — action needed.",
  customer_accepted: "The customer agreed to your counter price, but the order isn't finalized until you Accept it here.",
};

const TYPE_CONFIG: Record<
  OrderType,
  { label: string; bg: string; text: string }
> = {
  interstate_relocation: {
    label: "Interstate",
    bg: "bg-purple-100",
    text: "text-purple-800",
  },
  local_relocation: {
    label: "Local",
    bg: "bg-teal-100",
    text: "text-teal-800",
  },
};

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "accepted", label: "Accepted" },
  { value: "rejected", label: "Rejected" },
  { value: "counter_sent", label: "Counter Sent" },
  { value: "customer_countered", label: "Customer Countered" },
  { value: "delivered", label: "Delivered" },
  { value: "picked_up", label: "Picked Up" },
];

const TYPE_FILTERS: { value: TypeFilter; label: string }[] = [
  { value: "all", label: "All Types" },
  { value: "interstate_relocation", label: "Interstate" },
  { value: "local_relocation", label: "Local" },
];

// ── Summary Card ──────────────────────────────────────────────────────────────

const SummaryCard = ({
  label,
  value,
  icon: Icon,
  color,
  loading,
}: {
  label: string;
  value: number;
  icon: React.ElementType;
  color: string;
  loading?: boolean;
}) => (
  <motion.div
    className="bg-white rounded-[12px] shadow-sm p-4 flex items-center gap-4 border border-gray-200"
    whileHover={{ y: -4, boxShadow: "0 10px 28px rgba(20,56,135,0.13)" }}
    transition={{ type: "spring", stiffness: 300, damping: 20 }}
  >
    <div
      className={`w-11 h-11 rounded-lg flex items-center justify-center ${color}`}
    >
      <Icon size={20} className="text-white" />
    </div>
    <div>
      <p className="text-xs text-[#828282]">{label}</p>
      {loading ? (
        <div className="h-8 w-10 bg-gray-200 animate-pulse rounded mt-1" />
      ) : (
        <p className="text-2xl font-bold text-[#00032A]">{value}</p>
      )}
    </div>
  </motion.div>
);

// ── Order Detail Modal ────────────────────────────────────────────────────────

const DetailSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="bg-[#F5F6FA] rounded-lg p-4">
    <p className="text-[10px] text-[#828282] uppercase tracking-widest font-semibold mb-3">
      {title}
    </p>
    {children}
  </div>
);

const DetailRow = ({
  label,
  value,
  highlight,
}: {
  label: string;
  value: React.ReactNode;
  highlight?: boolean;
}) => (
  <div className="flex items-start justify-between gap-3 py-1.5 border-b border-gray-100 last:border-0">
    <span className="text-xs text-[#828282] shrink-0 w-40">{label}</span>
    <span
      className={`text-xs font-medium text-right ${highlight ? "text-[#143887]" : "text-[#00032A]"}`}
    >
      {value || "—"}
    </span>
  </div>
);

// ── Negotiation Step ─────────────────────────────────────────────────────────

const NegotiationStep = ({
  dot,
  label,
  sub,
  amount,
  isLast,
  isActive,
  labelColor,
  amountColor,
  badge,
  badgeColor,
}: {
  dot: string;
  label: string;
  sub?: string;
  amount: string;
  isLast?: boolean;
  isActive?: boolean;
  labelColor?: string;
  amountColor?: string;
  badge?: string;
  badgeColor?: string;
}) => (
  <div className="flex items-start gap-3">
    <div className="flex flex-col items-center shrink-0">
      <div className={`w-3 h-3 rounded-full border-2 ${dot} mt-1`} />
      {!isLast && <div className="w-px flex-1 min-h-[28px] bg-gray-200 my-1" />}
    </div>
    <div className="flex-1 pb-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            {badge && (
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${badgeColor}`}>
                {badge}
              </span>
            )}
            <p className={`text-xs font-semibold ${isActive ? (labelColor ?? "text-[#143887]") : "text-[#00032A]"}`}>
              {label}
            </p>
          </div>
          {sub && <p className="text-[11px] text-[#828282] mt-0.5 italic leading-4">{sub}</p>}
        </div>
        <span className={`text-sm font-bold whitespace-nowrap ${isActive ? (amountColor ?? "text-[#143887]") : "text-[#828282]"}`}>
          {amount}
        </span>
      </div>
    </div>
  </div>
);

// ── Order Detail Modal ────────────────────────────────────────────────────────

const OrderDetailModal = ({
  order: initialOrder,
  onClose,
}: {
  order: RelocationOrder;
  onClose: () => void;
}) => {
  const [mode, setMode] = useState<ActionMode>(null);
  const [counterPrice, setCounterPrice] = useState("");
  const [counterDesc, setCounterDesc] = useState("");
  const [counterError, setCounterError] = useState("");

  const { data: freshData, refetch: refetchOrder } = useGetRelocationOrderByIdQuery(initialOrder.id);
  const order: RelocationOrder = freshData?.data?.data ?? initialOrder;

  const [acceptOrder, { isLoading: accepting }] = useAcceptRelocationOrderMutation();
  const [rejectOrder, { isLoading: rejecting }] = useRejectRelocationOrderMutation();
  const [counterOrder, { isLoading: countering }] = useCounterRelocationOrderMutation();

  const { can } = usePermission();
  const canAccept = can("accept relocation order");
  const canReject = can("reject relocation order");
  const canCounter = can("send relocation counter offer");

  // On a 400 the order has moved on (or the customer asked to negotiate):
  // show the API's reason as-is and load the current state.
  const handleActionError = (err: any, fallback: string) => {
    toast.error(apiErrorMessage(err, fallback));
    if (err?.status === 400) {
      setMode(null);
      refetchOrder();
    }
  };

  const handleAccept = async () => {
    try {
      await acceptOrder({ id: order.id }).unwrap();
      toast.success("Order accepted successfully.");
      onClose();
    } catch (err) {
      handleActionError(err, "Failed to accept order. Please try again.");
    }
  };

  const handleReject = async () => {
    try {
      await rejectOrder({ id: order.id }).unwrap();
      toast.success("Order rejected.");
      onClose();
    } catch (err) {
      handleActionError(err, "Failed to reject order. Please try again.");
    }
  };

  const handleCounter = async () => {
    const val = parseFloat(counterPrice.replace(/,/g, ""));
    if (!val || val <= 0) {
      setCounterError("Please enter a valid amount.");
      return;
    }
    if (!counterDesc.trim()) {
      setCounterError("Please add a reason for the counter offer.");
      return;
    }
    try {
      await counterOrder({
        id: order.id,
        offered_price: val,
        description: counterDesc.trim(),
      }).unwrap();
      toast.success("Counter offer sent successfully.");
      onClose();
    } catch (err) {
      handleActionError(err, "Failed to send counter offer. Please try again.");
    }
  };

  const statusCfg = STATUS_CONFIG[order.status];
  const typeCfg = TYPE_CONFIG[order.order_type];
  const canAct = order.status === "pending" || order.status === "customer_countered" || order.status === "customer_accepted";
  const isBusy = accepting || rejecting || countering;
  // Customer tapped "Negotiate": no accepting at the original price until the company counters.
  const negotiationOpen = isNegotiationRequestOpen(order);
  // The customer's own price from the request, while it's still open.
  const proposedPrice = negotiationOpen ? order.negotiation_request?.proposed_price ?? null : null;
  // After agreement `price` is the agreed price, not the customer's original one.
  const isAgreed = order.status === "accepted" || order.status === "picked_up" || order.status === "delivered";
  const wasNegotiated = !!order.counter_offer || !!order.negotiation_request;

  const pickupLabel = order.route?.from || order.pickup_location || null;
  const destinationLabel = order.route?.to || order.destination || null;

  const scheduleDateType =
    order.pickup_date_type === "today"
      ? "Today (same-day move)"
      : order.pickup_date_type === "scheduled"
      ? "Scheduled date"
      : null;

  const hasAdditionalInfo =
    order.delivery_note ||
    order.inventory_description ||
    (order.extra_stops && order.extra_stops.length > 0) ||
    order.package_type ||
    order.package_weight ||
    order.package_size;

  const hasRecipient =
    order.recipient_name || order.recipient_phone || order.recipient_additional_info;

  // Build negotiation steps in correct chronological sequence
  type NegotiationEntry = {
    side: "company" | "customer";
    label: string;
    amount: string;
    note?: string;
    isLatest?: boolean;
    isAccepted?: boolean;
  };

  const negotiationSteps: NegotiationEntry[] = [];

  // Once agreed, `price` is the agreed amount; only show an original if the API sends one.
  const originalPrice = isAgreed && wasNegotiated ? order.original_price ?? null : order.price;
  if (originalPrice) {
    negotiationSteps.push({
      side: "customer",
      label: "Customer's Requested Price",
      amount: formatCurrency(originalPrice),
    });
  }

  if (order.negotiation_request?.proposed_price) {
    negotiationSteps.push({
      side: "customer",
      label: "Customer's Negotiation Offer",
      amount: formatCurrency(order.negotiation_request.proposed_price),
      isLatest: negotiationOpen,
    });
  }

  if (order.counter_offer) {
    const co = order.counter_offer;
    const proposedBy = co.proposed_by;
    const companyPrice = co.company_offered_price;
    const customerPrice = co.customer_offered_price;

    if (proposedBy === "company") {
      // Order: customer request → [customer counter] → company counter (latest)
      if (customerPrice) {
        negotiationSteps.push({
          side: "customer",
          label: "Customer's Counter Offer",
          amount: formatCurrency(customerPrice),
        });
      }
      if (companyPrice) {
        negotiationSteps.push({
          side: "company",
          label: "Your Counter Offer",
          amount: formatCurrency(companyPrice),
          note: co.description || undefined,
          isLatest: order.status !== "customer_accepted",
        });
      }
    } else {
      // Order: customer request → [company counter] → customer counter (latest)
      if (companyPrice) {
        negotiationSteps.push({
          side: "company",
          label: "Your Counter Offer",
          amount: formatCurrency(companyPrice),
        });
      }
      if (customerPrice) {
        negotiationSteps.push({
          side: "customer",
          label: "Customer's Counter Offer",
          amount: formatCurrency(customerPrice),
          note: co.description || undefined,
          isLatest: order.status !== "customer_accepted",
        });
      }
    }

    if (order.status === "customer_accepted") {
      negotiationSteps.push({
        side: "customer",
        label: "Customer Accepted",
        amount: formatCurrency(companyPrice ?? order.price),
        isLatest: true,
        isAccepted: true,
      });
    }
  }

  if (isAgreed && wasNegotiated) {
    negotiationSteps.push({
      side: "company",
      label: "Agreed Price",
      amount: formatCurrency(order.price),
      isLatest: true,
      isAccepted: true,
    });
  }

  const activePrice = proposedPrice ? proposedPrice : isAgreed ? order.price : order.counter_offer
    ? order.counter_offer.proposed_by === "company"
      ? order.counter_offer.company_offered_price ?? order.price
      : order.counter_offer.customer_offered_price ?? order.price
    : order.price;

  const hasCompanyCounter = Boolean(order.counter_offer?.company_offered_price);
  const hasCustomerCounter = Boolean(order.counter_offer?.customer_offered_price);

  return (
    <FleetrifyModal
      isOpen
      onClose={onClose}
      title={`Relocation Request — ${getDisplayId(order)}`}
      className="max-w-3xl"
    >
      <div className="flex flex-col lg:flex-row h-full max-h-[82vh] overflow-hidden">

        {/* ── LEFT COLUMN: Order Details ─────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4 border-r border-gray-100 min-w-0">

          {/* Status badges */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full ${statusCfg.bg} ${statusCfg.text}`}>
              {statusCfg.label}
              {STATUS_EXPLANATIONS[order.status] && (
                <InfoTooltip position="bottom" text={STATUS_EXPLANATIONS[order.status]!} />
              )}
            </span>
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full ${typeCfg.bg} ${typeCfg.text}`}>
              <Truck size={10} /> {typeCfg.label} Relocation
            </span>
            {showNegotiationBadge(order) && <NegotiationRequestBadge className="py-1" />}
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full ${
              order.payment_status === "paid"
                ? "bg-green-50 text-green-700 border border-green-200"
                : "bg-gray-100 text-gray-500"
            }`}>
              {order.payment_status === "paid" ? "Paid" : "Unpaid"}
              <InfoTooltip
                position="bottom"
                text="Shows whether the customer has completed payment for this order. An unpaid order that has already moved past acceptance is expected — not an error — when it's set to pay after delivery (see the badge alongside)."
              />
            </span>
            <PaymentBadges
              // Only forwarded for cash orders — PaymentBadges' cash-status
              // readout needs it, but this page already has its own
              // Paid/Unpaid badge above, so passing it for wallet orders
              // too would just duplicate that badge.
              paymentStatus={order.payment_method === "cash" ? order.payment_status : undefined}
              paymentPreference={order.payment_preference}
              paymentMethod={order.payment_method}
              cashCollected={order.cash_collected}
              customerConfirmedCashAt={order.customer_confirmed_cash_at}
            />
          </div>

          {/* Customer */}
          <DetailSection title="Customer Information">
            <DetailRow label="Full Name" value={order.customer?.name} />
            <DetailRow label="Email" value={order.customer?.email} />
            <DetailRow label="Phone" value={order.customer?.phone} />
            {order.contact_name && <DetailRow label="On-site Contact" value={order.contact_name} />}
            {order.contact_phone && <DetailRow label="Contact Phone" value={order.contact_phone} />}
          </DetailSection>

          {/* Move Schedule */}
          <DetailSection title="Move Schedule">
            {scheduleDateType && <DetailRow label="Schedule Type" value={scheduleDateType} />}
            <DetailRow
              label="Move Date"
              value={order.pickup_date ? formatDate(order.pickup_date) : "Not specified"}
              highlight={!!order.pickup_date}
            />
            <DetailRow
              label="Move Time"
              value={order.pickup_time ? order.pickup_time.slice(0, 5) : "Not specified"}
            />
          </DetailSection>

          {/* Pickup & Destination */}
          <DetailSection title="Pickup & Destination">
            <div className="flex items-start gap-3 py-1">
              <div className="flex flex-col items-center mt-0.5 shrink-0">
                <div className="w-2.5 h-2.5 rounded-full bg-[#143887]" />
                <div className="w-px h-7 bg-gray-300 my-0.5" />
                <MapPin size={12} className="text-[#143887]" />
              </div>
              <div className="flex-1 space-y-3">
                <div>
                  <p className="text-[10px] text-[#828282] uppercase tracking-wide font-medium">Pickup</p>
                  <p className="text-xs font-medium text-[#00032A] mt-0.5">{order.pickup_location || "—"}</p>
                </div>
                <div>
                  <p className="text-[10px] text-[#828282] uppercase tracking-wide font-medium">Destination</p>
                  <p className="text-xs font-medium text-[#00032A] mt-0.5">{order.destination || "—"}</p>
                </div>
              </div>
            </div>
            {order.route && (
              <div className="mt-2 pt-2 border-t border-gray-200 space-y-1">
                <DetailRow label="Route" value={`${order.route.from} → ${order.route.to}`} />
                <DetailRow label="Route Base Price" value={formatCurrency(order.route.price)} />
              </div>
            )}
          </DetailSection>

          {/* Move Details */}
          <DetailSection title="Move Details">
            {order.move_type && (
              <DetailRow label="Property Type" value={order.move_type.charAt(0).toUpperCase() + order.move_type.slice(1)} />
            )}
            {order.move_size && (
              <DetailRow label="Property Size" value={formatMoveSize(order.move_size)} />
            )}
            {order.vehicle_type && (
              <DetailRow label="Vehicle" value={order.vehicle_type.charAt(0).toUpperCase() + order.vehicle_type.slice(1)} />
            )}
            {order.has_loading_unloading !== null && (
              <DetailRow label="Loading & Unloading" value={order.has_loading_unloading ? "Yes — required" : "No"} />
            )}
            {order.has_packaging_materials !== null && (
              <DetailRow label="Packaging Materials" value={order.has_packaging_materials ? "Yes — required" : "No"} />
            )}
            {order.extra_stops && order.extra_stops.length > 0 && (
              <DetailRow label="Extra Stops" value={order.extra_stops.join(", ")} />
            )}
          </DetailSection>

          {/* Additional Info */}
          {hasAdditionalInfo && (
            <DetailSection title="Additional Information">
              {order.inventory_description && <DetailRow label="Inventory / Items" value={order.inventory_description} />}
              {order.delivery_note && <DetailRow label="Delivery Note" value={order.delivery_note} />}
              {order.package_type && <DetailRow label="Package Type" value={order.package_type} />}
              {order.package_weight && <DetailRow label="Package Weight" value={String(order.package_weight)} />}
              {order.package_size && <DetailRow label="Package Size" value={order.package_size} />}
            </DetailSection>
          )}

          {/* Recipient */}
          {hasRecipient && (
            <DetailSection title="Recipient at Destination">
              {order.recipient_name && <DetailRow label="Recipient Name" value={order.recipient_name} />}
              {order.recipient_phone && <DetailRow label="Recipient Phone" value={order.recipient_phone} />}
              {order.recipient_additional_info && <DetailRow label="Additional Info" value={order.recipient_additional_info} />}
            </DetailSection>
          )}
        </div>

        {/* ── RIGHT COLUMN: Negotiation + Actions ────────────────────────── */}
        <div className="w-full lg:w-[320px] shrink-0 flex flex-col overflow-y-auto p-5 space-y-4 bg-[#FAFBFD]">

          {/* Active price summary */}
          <div className={`rounded-xl p-4 text-white ${order.status === "customer_accepted" || (isAgreed && wasNegotiated) ? "bg-green-700" : "bg-[#143887]"}`}>
            <p className="text-[10px] uppercase tracking-widest font-semibold opacity-70 mb-1">
              {order.status === "customer_accepted"
                ? "Customer Accepted At"
                : isAgreed && wasNegotiated
                ? "Agreed Price"
                : proposedPrice
                ? "Customer's Offer"
                : !order.counter_offer
                ? "Customer's Quoted Price"
                : order.counter_offer.proposed_by === "company"
                ? "Your Current Offer"
                : "Customer's Latest Offer"}
            </p>
            <p className="text-2xl font-bold">{formatCurrency(activePrice)}</p>
            <div className="flex items-center gap-3 mt-1">
              {isAgreed && wasNegotiated ? (
                order.original_price && (
                  <p className="text-[11px] opacity-60">Originally {formatCurrency(order.original_price)}</p>
                )
              ) : (
                <p className="text-[11px] opacity-60">Customer's price: {formatCurrency(order.price)}</p>
              )}
              {order.route?.price && (
                <p className="text-[11px] opacity-60">Route rate: {formatCurrency(order.route.price)}</p>
              )}
            </div>
          </div>

          {order.negotiation_request && (
            <NegotiationRequestNotice request={order.negotiation_request} past={!showNegotiationBadge(order)} />
          )}

          {/* Negotiation timeline */}
          <div className="bg-white border border-gray-100 rounded-xl p-4">
            <p className="text-[10px] text-[#828282] uppercase tracking-widest font-semibold mb-4">
              Price Negotiation
            </p>

            {negotiationSteps.map((step, i) => {
              const isLast = i === negotiationSteps.length - 1;
              const isCompany = step.side === "company";
              const dotColor = step.isAccepted
                ? "border-green-500 bg-green-500"
                : isCompany
                ? "border-[#143887] bg-[#143887]"
                : "border-gray-400 bg-gray-400";
              const labelColor = step.isLatest
                ? isCompany ? "text-[#143887]" : step.isAccepted ? "text-green-700" : "text-orange-700"
                : "text-[#00032A]";
              const amountColor = step.isLatest
                ? isCompany ? "text-[#143887]" : step.isAccepted ? "text-green-700" : "text-orange-700"
                : "text-[#00032A]";
              return (
                <NegotiationStep
                  key={i}
                  dot={dotColor}
                  label={step.label}
                  sub={step.note ? `"${step.note}"` : undefined}
                  amount={step.amount}
                  isLast={isLast}
                  isActive={!!step.isLatest}
                  labelColor={labelColor}
                  amountColor={amountColor}
                  badge={isCompany ? "You" : "Customer"}
                  badgeColor={isCompany ? "bg-blue-50 text-[#143887]" : step.isAccepted ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-600"}
                />
              );
            })}

            {!order.counter_offer && (
              <p className="text-[11px] text-[#828282] mt-1">
                No counter offers exchanged yet.
              </p>
            )}
          </div>

          {/* Status banners (non-actionable states) */}
          {order.status === "counter_sent" && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-xs text-blue-800 leading-relaxed">
              Counter offer sent — waiting for the customer to respond.
            </div>
          )}

          {order.status === "accepted" && (
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-xs text-green-800">
              This order has been <span className="font-semibold">accepted</span>.
            </div>
          )}

          {order.status === "rejected" && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-xs text-red-800">
              This order has been <span className="font-semibold">rejected</span>.
            </div>
          )}

          {order.status === "customer_countered" && mode === null && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg px-4 py-3 text-xs text-orange-800 leading-relaxed">
              <p>
                The customer sent a counter offer of{" "}
                <span className="font-bold">
                  {order.counter_offer?.customer_offered_price
                    ? formatCurrency(order.counter_offer.customer_offered_price)
                    : "—"}
                </span>. Review and respond below.
              </p>
              {order.counter_offer?.description && (
                <p className="mt-2 pt-2 border-t border-orange-200 italic">
                  Customer's comment: "{order.counter_offer.description}"
                </p>
              )}
            </div>
          )}

          {order.status === "customer_accepted" && mode === null && (
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-xs text-green-800 leading-relaxed">
              The customer has <span className="font-semibold">accepted</span> your counter offer of{" "}
              <span className="font-bold">
                {order.counter_offer?.company_offered_price
                  ? formatCurrency(order.counter_offer.company_offered_price)
                  : formatCurrency(order.price)}
              </span>. Confirm or reject below to finalise this order.
            </div>
          )}

          {/* ── Action panel ── */}
          {canAct && mode === null && (
            <div className="space-y-2">
              <p className="text-[10px] text-[#828282] uppercase tracking-widest font-semibold">
                Respond to this Request
              </p>
              {negotiationOpen ? (
                <>
                  <p className="text-xs text-purple-800">{negotiationHint(order.negotiation_request)}</p>
                  {/* With a proposed price, the company can accept it outright. */}
                  {proposedPrice && (
                    <button
                      onClick={() => setMode("accept")}
                      disabled={isBusy || !canAccept}
                      title={canAccept ? "Accepts the customer's offered price and converts this request into a live order." : "You don't have permission to accept relocation orders."}
                      className="w-full py-2.5 rounded-lg bg-green-600 text-white text-sm font-medium flex items-center justify-center gap-2 hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      <CheckCircle size={15} /> Accept {formatCurrency(proposedPrice)}
                    </button>
                  )}
                  <button
                    onClick={() => setMode("counter")}
                    disabled={isBusy || !canCounter}
                    title={canCounter ? "Propose your price — you can accept once you and the customer agree." : "You don't have permission to send a counter offer."}
                    className="w-full py-2.5 rounded-lg bg-[#143887] text-white text-sm font-medium flex items-center justify-center gap-2 hover:bg-[#0f2d6b] transition-colors disabled:opacity-50"
                  >
                    <DollarSign size={15} /> Send Counter Offer
                  </button>
                </>
              ) : (
              <>
              <button
                onClick={() => setMode("accept")}
                disabled={isBusy || !canAccept}
                title={canAccept ? "Confirms the current price and converts this request into a live order." : "You don't have permission to accept relocation orders."}
                className="w-full py-2.5 rounded-lg bg-[#143887] text-white text-sm font-medium flex items-center justify-center gap-2 hover:bg-[#0f2d6b] transition-colors disabled:opacity-50"
              >
                <CheckCircle size={15} /> Accept Offer
              </button>
              {order.status !== "customer_accepted" && (
                <button
                  onClick={() => setMode("counter")}
                  disabled={isBusy || !canCounter}
                  title={canCounter ? "Propose a different price to the customer — the request stays open until they respond." : "You don't have permission to send a counter offer."}
                  className="w-full py-2.5 rounded-lg border border-[#143887] text-[#143887] text-sm font-medium flex items-center justify-center gap-2 hover:bg-blue-50 transition-colors disabled:opacity-50"
                >
                  <DollarSign size={15} /> Send Counter Offer
                </button>
              )}
              </>
              )}
              <button
                onClick={() => setMode("reject")}
                disabled={isBusy || !canReject}
                title={canReject ? "Permanently declines the request — the customer is notified and no order is created." : "You don't have permission to reject relocation orders."}
                className="w-full py-2.5 rounded-lg border border-red-300 text-red-600 text-sm font-medium flex items-center justify-center gap-2 hover:bg-red-50 transition-colors disabled:opacity-50"
              >
                <XCircle size={15} /> Reject Request
              </button>
            </div>
          )}

          {/* Accept confirmation */}
          {mode === "accept" && (!negotiationOpen || !!proposedPrice) && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-3"
            >
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-green-600 shrink-0" />
                <p className="text-sm font-semibold text-green-800">Confirm Acceptance</p>
              </div>
              <p className="text-xs text-green-700 leading-relaxed">
                You are accepting at{" "}
                <span className="font-bold">{formatCurrency(activePrice)}</span>. This action
                cannot be undone.
              </p>
              <div className="flex gap-2 pt-1">
                <button
                  onClick={handleAccept}
                  disabled={accepting || !canAccept}
                  className="flex-1 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {accepting && <Loader2 size={13} className="animate-spin" />}
                  Confirm
                </button>
                <button
                  onClick={() => setMode(null)}
                  disabled={accepting}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Back
                </button>
              </div>
            </motion.div>
          )}

          {/* Reject confirmation */}
          {mode === "reject" && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-3"
            >
              <div className="flex items-center gap-2">
                <XCircle size={16} className="text-red-600 shrink-0" />
                <p className="text-sm font-semibold text-red-800">Confirm Rejection</p>
              </div>
              <p className="text-xs text-red-700 leading-relaxed">
                Are you sure you want to reject this order? The customer will be notified.
              </p>
              <div className="flex gap-2 pt-1">
                <button
                  onClick={handleReject}
                  disabled={rejecting || !canReject}
                  className="flex-1 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {rejecting && <Loader2 size={13} className="animate-spin" />}
                  Confirm
                </button>
                <button
                  onClick={() => setMode(null)}
                  disabled={rejecting}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Back
                </button>
              </div>
            </motion.div>
          )}

          {/* Counter offer form */}
          {mode === "counter" && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-blue-200 rounded-xl p-4 space-y-3"
            >
              <div className="flex items-center gap-2">
                <DollarSign size={15} className="text-[#143887] shrink-0" />
                <p className="text-sm font-semibold text-[#143887]">New Counter Offer</p>
              </div>

              {/* Reference prices */}
              <div className="bg-[#F5F6FA] rounded-lg px-3 py-2 space-y-1">
                <div className="flex justify-between text-[11px]">
                  <span className="text-[#828282]">Customer's price</span>
                  <span className="font-medium text-[#00032A]">{formatCurrency(order.price)}</span>
                </div>
                {proposedPrice && (
                  <div className="flex justify-between text-[11px]">
                    <span className="text-[#828282]">Customer's offer</span>
                    <span className="font-medium text-purple-700">{formatCurrency(proposedPrice)}</span>
                  </div>
                )}
                {hasCompanyCounter && order.counter_offer?.company_offered_price && (
                  <div className="flex justify-between text-[11px]">
                    <span className="text-[#828282]">Your last offer</span>
                    <span className="font-medium text-blue-700">{formatCurrency(order.counter_offer.company_offered_price)}</span>
                  </div>
                )}
                {hasCustomerCounter && order.counter_offer?.customer_offered_price && (
                  <div className="flex justify-between text-[11px]">
                    <span className="text-[#828282]">Customer's counter</span>
                    <span className="font-medium text-orange-700">{formatCurrency(order.counter_offer.customer_offered_price)}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="text-[11px] text-[#828282] font-medium block mb-1">
                  Your Offer Amount (₦)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#828282] font-medium">₦</span>
                  <input
                    type="number"
                    placeholder="e.g. 150000"
                    value={counterPrice}
                    onChange={(e) => { setCounterPrice(e.target.value); setCounterError(""); }}
                    className="w-full pl-7 pr-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#143887]/30 focus:border-[#143887]"
                  />
                </div>
              </div>

              <div>
                <label className="text-[11px] text-[#828282] font-medium block mb-1">
                  Reason / Note to Customer
                </label>
                <textarea
                  rows={3}
                  placeholder="e.g. Fuel surcharge applied due to long distance"
                  value={counterDesc}
                  onChange={(e) => { setCounterDesc(e.target.value); setCounterError(""); }}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#143887]/30 focus:border-[#143887] resize-none"
                />
              </div>

              {counterError && (
                <p className="text-xs text-red-600 flex items-center gap-1">
                  <AlertCircle size={12} /> {counterError}
                </p>
              )}

              <div className="flex gap-2 pt-1">
                <button
                  onClick={handleCounter}
                  disabled={countering || !canCounter}
                  className="flex-1 py-2 rounded-lg bg-[#143887] text-white text-sm font-medium hover:bg-[#0f2d6b] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {countering && <Loader2 size={13} className="animate-spin" />}
                  Send Counter
                </button>
                <button
                  onClick={() => setMode(null)}
                  disabled={countering}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Back
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </FleetrifyModal>
  );
};

// ── Table Skeleton ────────────────────────────────────────────────────────────

const TableSkeleton = () => (
  <>
    {Array.from({ length: 5 }).map((_, i) => (
      <tr key={i} className="border-b border-gray-100">
        {Array.from({ length: 8 }).map((__, j) => (
          <td key={j} className="px-4 py-3">
            <div className="h-4 bg-gray-200 animate-pulse rounded w-3/4" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

// ── Main Page ─────────────────────────────────────────────────────────────────

const RelocationQuotes = () => {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [sort, setSort] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState<RelocationOrder | null>(
    null
  );
  // ?order={id} opens that order, e.g. from a "negotiation requested" notification.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedOrderId = searchParams.get("order");
  const { data: linkedOrderData } = useGetRelocationOrderByIdQuery(linkedOrderId ?? "", { skip: !linkedOrderId });
  useEffect(() => {
    const linked = linkedOrderData?.data?.data;
    if (linkedOrderId && linked && linked.id === linkedOrderId) setSelectedOrder(linked);
  }, [linkedOrderId, linkedOrderData]);
  const closeOrder = () => {
    setSelectedOrder(null);
    if (linkedOrderId) {
      searchParams.delete("order");
      setSearchParams(searchParams, { replace: true });
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const queryParams: RelocationOrdersParams = {
    ...(typeFilter !== "all" && { order_type: typeFilter }),
    ...(debouncedSearch && { search: debouncedSearch }),
    sort,
    per_page: 15,
    page,
  };

  const { data, isLoading, isFetching, isError, refetch } =
    useGetRelocationOrdersQuery(queryParams);

  const counts = data?.data?.counts ?? {
    total: 0,
    pending: 0,
    accepted: 0,
    rejected: 0,
    counter_sent: 0,
  };

  const allOrders = data?.data?.data ?? [];
  const meta = data?.data?.meta;

  const filteredOrders =
    statusFilter === "all"
      ? allOrders
      : allOrders.filter((o) => o.status === statusFilter);

  const handleClearSearch = useCallback(() => {
    setSearchInput("");
    setDebouncedSearch("");
  }, []);

  return (
    <DashboardLayout
      title="Relocation Orders"
      description="Review and respond to relocation service quote requests from customers."
      containerClass="pb-10"
    >
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <SummaryCard
          label="Total Orders"
          value={counts.total}
          icon={Truck}
          color="bg-[#143887]"
          loading={isLoading}
        />
        <SummaryCard
          label="Pending"
          value={counts.pending}
          icon={Clock}
          color="bg-yellow-500"
          loading={isLoading}
        />
        <SummaryCard
          label="Accepted"
          value={counts.accepted}
          icon={CheckCircle}
          color="bg-green-600"
          loading={isLoading}
        />
        <SummaryCard
          label="Rejected"
          value={counts.rejected}
          icon={XCircle}
          color="bg-red-500"
          loading={isLoading}
        />
      </div>

      {/* Table Card */}
      <FleetrifyCard cardContainerClass="mt-8">
        {/* Filters Row */}
        <div className="flex flex-col gap-3 mb-6">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            {/* Search */}
            <div className="relative w-full sm:w-72">
              <Search
                size={15}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#828282]"
              />
              <input
                type="text"
                placeholder="Search name, order ID, or location..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#143887]"
              />
              {searchInput && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  <X size={13} className="text-[#828282]" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-2">
              {/* Sort */}
              <button
                onClick={() =>
                  setSort((prev) => (prev === "desc" ? "asc" : "desc"))
                }
                className="flex items-center gap-1.5 px-3 py-2 text-xs border border-gray-300 rounded-md text-[#828282] hover:bg-gray-50 transition-colors"
              >
                {sort === "desc" ? "Newest First" : "Oldest First"}
              </button>

              {/* Refresh */}
              <button
                onClick={() => refetch()}
                disabled={isFetching}
                className="p-2 border border-gray-300 rounded-md text-[#828282] hover:bg-gray-50 transition-colors disabled:opacity-50"
                title="Refresh"
              >
                <RefreshCw
                  size={14}
                  className={isFetching ? "animate-spin" : ""}
                />
              </button>
            </div>
          </div>

          {/* Type + Status filter chips */}
          <div className="flex flex-wrap gap-2">
            {TYPE_FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => {
                  setTypeFilter(f.value);
                  setPage(1);
                }}
                className={`px-3 py-1.5 text-xs rounded-full font-medium transition-colors ${
                  typeFilter === f.value
                    ? "bg-[#00032A] text-white"
                    : "bg-gray-100 text-[#828282] hover:bg-gray-200"
                }`}
              >
                {f.label}
              </button>
            ))}

            <span className="w-px h-5 bg-gray-200 self-center mx-1" />

            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setStatusFilter(f.value)}
                className={`px-3 py-1.5 text-xs rounded-full font-medium transition-colors ${
                  statusFilter === f.value
                    ? "bg-[#143887] text-white"
                    : "bg-gray-100 text-[#828282] hover:bg-gray-200"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Error State */}
        {isError && (
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <AlertCircle size={36} className="text-red-400" />
            <p className="text-sm font-medium text-red-700">
              Failed to load relocation orders.
            </p>
            <button
              onClick={() => refetch()}
              className="px-4 py-2 text-sm rounded-md border border-red-300 text-red-600 hover:bg-red-50 transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Table */}
        {!isError && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F8F9FA] border-b border-gray-200">
                  {[
                    "Order ID",
                    "Customer",
                    "Type",
                    "From → To",
                    "Quoted Amount",
                    "Move Date",
                    "Status",
                    "Action",
                  ].map((h) => (
                    <th
                      key={h}
                      className="text-left px-4 py-3 text-xs font-semibold text-[#828282] uppercase tracking-wide whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {isLoading ? (
                  <TableSkeleton />
                ) : filteredOrders.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center py-12 text-[#828282] text-sm"
                    >
                      {debouncedSearch || statusFilter !== "all" || typeFilter !== "all"
                        ? "No orders match your filters."
                        : "No relocation orders found."}
                    </td>
                  </tr>
                ) : (
                  filteredOrders.map((order) => {
                    const statusCfg = STATUS_CONFIG[order.status];
                    const typeCfg = TYPE_CONFIG[order.order_type];
                    const fromLabel = order.route?.from ?? "—";
                    const toLabel = order.route?.to ?? "—";

                    return (
                      <motion.tr
                        key={order.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="hover:bg-[#F5F6FA] transition-colors cursor-pointer"
                        onClick={() => setSelectedOrder(order)}
                      >
                        <td className="px-4 py-3 font-medium text-[#143887]">
                          {getDisplayId(order)}
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-[#00032A]">
                            {order.customer?.name ?? "Unknown customer"}
                          </p>
                          <p className="text-xs text-[#828282]">
                            {order.customer?.email ?? "—"}
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeCfg.bg} ${typeCfg.text}`}
                          >
                            {typeCfg.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5 text-xs text-[#828282] max-w-[200px]">
                            <span className="truncate">{fromLabel}</span>
                            <ArrowRight
                              size={12}
                              className="shrink-0 text-[#143887]"
                            />
                            <span className="truncate">{toLabel}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 font-semibold text-[#00032A] whitespace-nowrap">
                          {formatCurrency(order.price)}
                          {order.counter_offer?.company_offered_price && (
                            <p className="text-xs font-normal text-blue-600">
                              Your offer: {formatCurrency(order.counter_offer.company_offered_price)}
                            </p>
                          )}
                          {order.counter_offer?.customer_offered_price ? (
                            <p className="text-xs font-normal text-orange-600">
                              Cust. counter: {formatCurrency(order.counter_offer.customer_offered_price)}
                            </p>
                          ) : order.negotiation_request?.proposed_price && (
                            <p className="text-xs font-normal text-purple-700">
                              Cust. offer: {formatCurrency(order.negotiation_request.proposed_price)}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-[#828282] whitespace-nowrap">
                          {formatDate(order.pickup_date)}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${statusCfg.bg} ${statusCfg.text}`}
                          >
                            {statusCfg.label}
                          </span>
                          {showNegotiationBadge(order) && (
                            <div className="mt-1">
                              <NegotiationRequestBadge />
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedOrder(order);
                            }}
                            className="p-1.5 rounded-md hover:bg-[#e4ebf8] text-[#143887] transition-colors"
                            title="View details"
                          >
                            <Eye size={15} />
                          </button>
                        </td>
                      </motion.tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer: count + pagination */}
        {!isError && !isLoading && (
          <div className="flex items-center justify-between mt-4 flex-wrap gap-3">
            <p className="text-xs text-[#828282]">
              Showing {filteredOrders.length} of {meta?.total ?? allOrders.length} order
              {(meta?.total ?? allOrders.length) !== 1 ? "s" : ""}
            </p>

            {meta && meta.last_page > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1 || isFetching}
                  className="p-1.5 rounded border border-gray-300 text-[#828282] hover:bg-gray-50 disabled:opacity-40 transition-colors"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className="text-xs text-[#828282]">
                  Page {meta.current_page} of {meta.last_page}
                </span>
                <button
                  onClick={() =>
                    setPage((p) => Math.min(meta.last_page, p + 1))
                  }
                  disabled={page === meta.last_page || isFetching}
                  className="p-1.5 rounded border border-gray-300 text-[#828282] hover:bg-gray-50 disabled:opacity-40 transition-colors"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            )}
          </div>
        )}
      </FleetrifyCard>

      {selectedOrder && (
        <OrderDetailModal
          order={selectedOrder}
          onClose={closeOrder}
        />
      )}
    </DashboardLayout>
  );
};

export default RelocationQuotes;
